import React, { useState, useEffect } from 'react';

export const CheckInput = React.memo(({ name, value, onChange, isEditMode = true }) => {
  return (
    <div className="p-2">
      <label className="mb-1 inline-flex items-center text-sm font-medium">
        <input
          type="checkbox"
          disabled={!isEditMode}
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="mr-2"
        />
        {name}
      </label>
    </div>
  );
});

export const CheckInputSection = React.memo(({ name, data, onChange, isEditMode }) => {
  return (
    <div className="rounded-md border p-2">
      <label className="text-md mb-1 block rounded bg-gray-200 p-2 font-medium">
        {name}
      </label>

      {data.map((item, index) => (
        <CheckInput
          key={item.key || index}
          name={item.name}
          value={item.value}
          isEditMode={isEditMode}
          onChange={(val) => onChange(item.key, val)}
        />
      ))}
    </div>
  );
});

export const Input = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  isViewOnly = false,
  type = "text",
  value,
  onChange,
  placeholder = "",
  className = "", parentClassName = "",
  error = false,
  errorMessage = "",
  ...props
}) => {
  
  const handleChange = (e) => {
    if (type === "number") {
      const value = e.target.value;

      // Allow empty value for editing
      if (value === "") {
        onChange?.(e);
        return;
      }

      // Only positive numbers and decimals
      if (!/^\d*\.?\d*$/.test(value)) {
        return;
      }

      if (Number(value) < 0) {
        return;
      }
    }

    onChange?.(e);
  };

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Input */}
      <input
        type={type}
        value={value}
        onChange={handleChange}
        min="0"
        onKeyDown={(e) => {
          if (type === "number") {
            const blockedKeys = ["e", "E", "+", "-"];

            if (blockedKeys.includes(e.key)) {
              e.preventDefault();
            }

            // Prevent ArrowDown from decrementing below 0
            if (e.key === "ArrowDown" && Number(value || 0) <= 0) {
              e.preventDefault();
            }
          }

          props.onKeyDown?.(e);
        }}
        onPaste={(e) => {
          if (type === "number") {
            e.preventDefault();
          }

          props.onPaste?.(e);
        }}
        placeholder={placeholder}
        disabled={isEditMode || isDisabled || isViewOnly}
        className={`w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? "border-red-500" : ""} ${className}`}
        {...props}
      />

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};

export const TextArea = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  isViewOnly = false,
  value,
  onChange,
  placeholder = "",
  className = "",
  errorMessage = "",
  containerClassName = "", // <--- Add this prop
  error = false,
  rows = 4,
  ...props
}) => {
  const disabled = isEditMode || isDisabled || isViewOnly;

  return (
    // Add containerClassName here and make it flex if needed
    <div className={`flex flex-col ${containerClassName}`}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600 flex-shrink-0">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        // flex-grow allows the textarea to fill the div
        className={`w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? "border-red-500" : ""} ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"} ${className}`}
        {...props}
      />

      {error && <span className="mt-1 block text-sm text-red-500 flex-shrink-0">{errorMessage}</span>}
    </div>
  );
};

export const FlexibleTextArea = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  placeholder = "",
  className = "", error = false, errorMessage = "",
  rows = 4,
  ...props
}) => {
  const disabled = isEditMode || isDisabled;

  return (
    <div className="mb-1 flex h-full flex-col"> {/* flexible parent */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Flexible textarea */}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={`flex-1 w-full min-h-[3rem] box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y ${error ? "border-red-500" : ""} ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"} ${className}`}
        {...props}
      />

      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};

export const TimeInput = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  placeholder = "",
  className = "", error = false, errorMessage = "",
  ...props
}) => {
  return (
    <div className="mb-1">
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Time Input */}
      <input
        type="time"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={isEditMode || isDisabled}
        className={`w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? "border-red-500" : ""} ${className}`}
        {...props}
      />

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};

export const DateInput = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  isViewOnly = false,
  value,
  onChange,
  placeholder = "",
  className = "", error = false, errorMessage = "",
  ...props
}) => {
  // MODIFIED: Sanitize value to handle ISO format (YYYY-MM-DDTHH:mm:ss)
  // HTML5 Date input strictly requires YYYY-MM-DD format
  const sanitizedValue = (value && typeof value === 'string' && value.includes('T'))
    ? value.split('T')[0]
    : value || "";

  return (
    <div className="mb-1">
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Date Input */}
      <input
        type="date"
        value={sanitizedValue} // MODIFIED
        onChange={onChange ?? (() => { })}
        placeholder={placeholder}
        disabled={isEditMode || isDisabled || isViewOnly}
        className={`w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? "border-red-500" : ""} ${className}`}
        {...props}
      />

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};

export function DateTimeInput({
  date,
  setDate,
  placeholder = "Select a date", // Default placeholder
  className = "", // Default empty string for className
  ...props
}) {
  const [isSelected, setIsSelected] = useState(false);

  const handleDateTimeChange = (event) => {
    setDate(event.target.value);
    setIsSelected(true);
  };

  useEffect(() => {
    if (date !== undefined && date !== "") {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [date]);

  let formattedDate = "";
  if (date) {
    // Assuming 'date' is an ISO string like "2023-10-27T10:30:00"
    formattedDate = date.split('T')[0];
  }

  // Combine your default classes with the one passed via props
  const inputClasses = `w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`;

  return (
    <div>
      <input
        type="date"
        name="datetime"
        value={formattedDate}
        onChange={handleDateTimeChange}
        placeholder={placeholder} // Apply the placeholder prop
        className={inputClasses} // Apply the combined classes
        {...props}
      />
    </div>
  );
}

export function DateInputOld(date, setDate, isEditMode) {
  const [isSelected, setIsSelected] = useState(false);

  const handleDateTimeChange = (event) => {
    setDate(event.target.value);
    setIsSelected(true);
  };

  useEffect(() => {
    if (date !== undefined && date !== "" && date !== null) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [date]);

  let formattedDate = "";

  if (date) {
    formattedDate = date.split('T')[0];
  }

  return (
    <div>
      <input
        readOnly={!isEditMode}
        type="date"
        name="datetime"
        value={formattedDate || ''}
        onChange={handleDateTimeChange}
        style={{
          width: "90%",
          height: "30px",
          borderRadius: "3px",
          border: "1px solid #80808080",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)",
          fontFamily: "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif"
        }}
      />
    </div>
  );
}

export function DateInputOld2({ id, value, onChange }) {
  const [isSelected, setIsSelected] = useState(false);

  const handleDateTimeChange = (event) => {
    onChange(event.target.value);
    setIsSelected(true);
  };

  useEffect(() => {
    if (value !== undefined && value !== "" && value !== null) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [value]);

  let formattedDate = "";

  if (value) {
    formattedDate = value.split('T')[0];
  }

  return (
    <div>
      <input
        type="date"
        id={"AddEmployee-" + id}
        name="datetime"
        value={formattedDate || ''}
        onChange={handleDateTimeChange}
        style={{
          width: "90%",
          height: "30px",
          borderRadius: "3px",
          border: "1px solid #80808080",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)",
          fontFamily: "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif"
        }}
      />
    </div>
  );
}

export const NumberInput = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  placeholder = "",
  className = "", error = false, errorMessage = "",
  min,
  max,
  step,
  ...props
}) => {
  
  return (
    <div className="mb-1">
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Number Input */}
      <input
        type="number"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={isEditMode || isDisabled}
        min={min}
        max={max}
        step={step}
        className={`w-full box-border border border-solid border-gray-300 rounded-md px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? "border-red-500" : ""} ${className}`}
        {...props}
      />

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};

export const Checkbox = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  checked,
  onChange,
  className = "", error = false, errorMessage = "",
  ...props
}) => {
  return (
    <div className="mb-1">
      <label className="flex cursor-pointer items-center space-x-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={isEditMode || isDisabled}
          className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${error ? "border-red-500" : ""} ${className}`}
          {...props}
        />
        <span className="text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </span>
      </label>

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};


export const DaysOfWeekSelector = ({ value = {}, onChange, disabled = false }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default days structure (8 days including "All")
  const defaultDays = [
    { id: 'all', label: 'All Days', field: 'hasAllDays', value: 0 },
    { id: 'sun', label: 'Sunday', field: 'hasSunday', value: 1 },
    { id: 'mon', label: 'Monday', field: 'hasMonday', value: 2 },
    { id: 'tue', label: 'Tuesday', field: 'hasTuesday', value: 3 },
    { id: 'wed', label: 'Wednesday', field: 'hasWednesday', value: 4 },
    { id: 'thu', label: 'Thursday', field: 'hasThursday', value: 5 },
    { id: 'fri', label: 'Friday', field: 'hasFriday', value: 6 },
    { id: 'sat', label: 'Saturday', field: 'hasSaturday', value: 7 }
  ];

  useEffect(() => {
    setDays(defaultDays);
  }, []);

  const handleDayChange = (fieldName, isChecked) => {
    const newValue = { ...value, [fieldName]: isChecked };

    // Special handling for "All Days" checkbox
    if (fieldName === 'hasAllDays' && isChecked) {
      // If "All Days" is checked, check all individual days
      defaultDays.forEach(day => {
        if (day.field !== 'hasAllDays') {
          newValue[day.field] = true;
        }
      });
    } else if (fieldName !== 'hasAllDays' && !isChecked && value.hasAllDays) {
      // If any individual day is unchecked while "All Days" is checked, uncheck "All Days"
      newValue.hasAllDays = false;
    }

    onChange(newValue);
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-300 p-4">
        <div className="flex h-20 items-center justify-center">
          <div className="text-gray-500">Loading days...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-100 p-4 transition-colors hover:border-blue-500">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
        {days.map(day => (
          <div key={day.id} className="flex flex-col items-center">
            <div className="mb-1 flex items-center">
              <input
                type="checkbox"
                id={day.id}
                checked={value[day.field] || false}
                onChange={(e) => handleDayChange(day.field, e.target.checked)}
                disabled={disabled}
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label
                htmlFor={day.id}
                className={`ml-2 text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}
              >
                {day.label}
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RadioGroup = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  options = [],
  className = "", error = false, errorMessage = "",
  ...props
}) => {
  const disabled = isEditMode || isDisabled;

  return (
    <div className="mb-1">
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Radio Options */}
      <div className={`space-y-2 ${className}`}>
        {options.map((option) => (
          <label key={option.value} className="flex cursor-pointer items-center space-x-2">
            <input
              type="radio"
              value={option.value}
              checked={value === option.value}
              onChange={onChange}
              disabled={disabled}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              {...props}
            />
            <span className={`text-sm text-gray-600 ${disabled ? "text-gray-400" : ""}`}>
              {option.label}
            </span>
          </label>
        ))}
      </div>

      {/* Error message */}
      {error && <span className="mt-1 block text-sm text-red-500">{errorMessage}</span>}
    </div>
  );
};


export const CheckInputSectionAccessRight = React.memo(({ name, data, onChangeAccessRight, onChangeAccessType, isEditMode = true }) => {
  return (
    <div className="rounded-md border p-2">
      {/* Section header */}
      <label className="text-md mb-2 block rounded bg-gray-200 p-2 font-medium">{name}</label>

      {/* Header row for sub-checkboxes */}
      <div className="mb-1 flex items-center px-2 text-sm font-semibold text-gray-600">
        <div className="min-w-[200px] flex-1">Access Item</div>
        <div className="flex gap-3 pr-2">
          {data[0].accessType?.map((sub) => (
            <span key={sub.key} className="w-12 text-center">{sub.name}</span>
          ))}
        </div>
      </div>

      {/* Rows */}
      {data.map((item) => (
        <div key={item.key} className="flex items-center border-b px-2 py-1 hover:bg-gray-50">
          {/* Main Access Checkbox */}
          <div className="flex flex-1 items-center gap-1">
            <input
              type="checkbox"
              checked={item.value}
              disabled={!isEditMode}
              onChange={(e) => onChangeAccessRight(item.key, e.target.checked)}
              className="h-4 w-4"
            />
            <span className="truncate">{item.name}</span>
          </div>

          {/* Sub-checkboxes */}
          <div className="flex gap-3 pr-2">
            {item.accessType?.map((sub) => (
              <div key={sub.key} className="flex w-12 justify-center">
                <input
                  type="checkbox"
                  checked={sub.value}
                  disabled={!isEditMode}
                  onChange={(e) => onChangeAccessType(item.key, sub.key, e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});